import { createRouter, createWebHashHistory, createWebHistory, type RouteLocationNormalized, type NavigationGuardNext, type RouteRecordNormalized } from 'vue-router'
import OTPLogin from '../views/OTPLogin.vue'
import ForgotPassword from '../views/ForgotPassword.vue'
import ResetPassword from '../views/ResetPassword.vue'
import { useAuthStore } from '../stores/auth'
import { getCsrfToken } from '../utils/csrfToken'
import { ROLES, ADMIN_ACCESS_ROLES, hasPermission, isAdminFamilyRole } from '../constants/roles'

const Login = () => import('../views/Login.vue')
const Dashboard = () => import('../views/Dashboard.vue')
const SchoolList = () => import('../views/SchoolList.vue')
const SchoolForm = () => import('../views/SchoolForm.vue')
const TeacherList = () => import('../views/TeacherList.vue')
const TeacherForm = () => import('../views/TeacherForm.vue')
const SeniorityList = () => import('../views/SeniorityList.vue')
const MasterData = () => import('../views/MasterData.vue')
const MedicalRecords = () => import('../views/MedicalRecords.vue')
const UserManagement = () => import('../views/UserManagement.vue')
const TransferReports = () => import('../views/TransferReports.vue')
const AuditLogs = () => import('../views/AuditLogs.vue')
const MenuManagement = () => import('../views/MenuManagement.vue')
const WorkflowManagement = () => import('../views/WorkflowManagement.vue')
const HardDeleteRequests = () => import('../views/HardDeleteRequests.vue')
const MuTchIngest = () => import('../views/MuTchIngest.vue')
const TransferApplicationApproval = () => import('../views/TransferApplicationApproval.vue')
const TransferOrderManagement = () => import('../views/TransferOrderManagement.vue')
const ApproverAssignment = () => import('../views/ApproverAssignment.vue')
const JoiningReportUpload = () => import('../views/JoiningReportUpload.vue')
const JoiningReportsList = () => import('../views/JoiningReportsList.vue')
const NonTeachingStaff = () => import('../views/NonTeachingStaff.vue')
const StaffIDGenerate = () => import('../views/StaffIDGenerate.vue')
const StaffIDList = () => import('../views/StaffIDList.vue')
const StaffIDVerify = () => import('../views/StaffIDVerify.vue')
/** Includes legacy `admin` so route `meta.roles` checks match User Management roles. */
const ADMIN_ROLES_MUTABLE = [...ADMIN_ACCESS_ROLES, 'admin']
const ADMIN_ROLES_WITH_LEGACY_MUTABLE = [...ADMIN_ROLES_MUTABLE]
const withAdminRoles = (...extra: string[]) => [...ADMIN_ROLES_MUTABLE, ...extra]

// Extend RouteMeta interface
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    permissions?: string[]
    roles?: string[]
  }
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false }
  },
  {
    path: '/otp-login',
    name: 'OTPLogin',
    component: OTPLogin,
    meta: { requiresAuth: false }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false }
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/schools',
    name: 'SchoolList',
    component: SchoolList,
    meta: { 
      requiresAuth: true,
      permissions: ['schools.read']
    }
  },
  {
    path: '/schools/new',
    name: 'SchoolNew',
    component: SchoolForm,
    meta: { 
      requiresAuth: true,
      permissions: ['schools.create']
    }
  },
  {
    path: '/schools/:id/edit',
    name: 'SchoolEdit',
    component: SchoolForm,
    props: true,
    meta: { 
      requiresAuth: true,
      permissions: ['schools.update']
    }
  },
  {
    path: '/teachers',
    name: 'TeacherList',
    component: TeacherList,
    meta: { 
      requiresAuth: true,
      permissions: ['teachers.read']
    }
  },
  {
    path: '/teachers/new',
    name: 'TeacherNew',
    component: TeacherForm,
    meta: { 
      requiresAuth: true,
      permissions: ['teachers.create']
    }
  },
  {
    path: '/teachers/:id/edit',
    name: 'TeacherEdit',
    component: TeacherForm,
    props: true,
    meta: { 
      requiresAuth: true,
      permissions: ['teachers.update']
    }
  },
  {
    path: '/teachers/seniority',
    name: 'SeniorityList',
    component: SeniorityList,
    meta: { 
      requiresAuth: true,
      permissions: ['teachers.read']
    }
  },
  {
    path: '/teacher-profile',
    name: 'TeacherProfile',
    component: () => import('../views/TeacherProfile.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher']
    }
  },
  {
    path: '/teacher-profile/edit',
    name: 'TeacherProfileEdit',
    component: () => import('../views/SimpleProfileEdit.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher']
    }
  },
  {
    path: '/account-profile',
    name: 'AccountProfile',
    component: () => import('../views/UserAccountProfile.vue'),
    meta: {
      requiresAuth: true,
      roles: withAdminRoles('deo', 'sdeo', 'hoi', 'minister', 'secretariat')
    }
  },
  {
    path: '/posting-history',
    name: 'TeacherPostingHistory',
    component: () => import('../views/PostingHistory.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher', 'minister', 'hoi', 'deo', 'sdeo', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/class-enrolment',
    name: 'ClassEnrolment',
    component: () => import('../views/ClassEnrolment.vue'),
    meta: {
      requiresAuth: true,
      roles: ['hoi', 'super_admin', 'admin_directorate', 'admin_secretariat', 'deo', 'sdeo', 'admin', 'minister', 'secretariat']
    }
  },
  {
    path: '/school-strength-report',
    name: 'SchoolStrengthReport',
    component: () => import('../views/SchoolStrengthReport.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'admin_directorate', 'admin_secretariat', 'minister', 'deo', 'sdeo', 'secretariat', 'hoi', 'admin']
    }
  },

  {
    path: '/transfer-application',
    name: 'TransferApplication',
    redirect: '/transfer-application/apply',
    meta: { 
      requiresAuth: true,
      roles: ['teacher']
    }
  },
  {
    path: '/transfer-application/apply',
    name: 'TransferApplicationApply',
    component: () => import('../views/TransferApplication.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher']
    }
  },
  {
    path: '/transfer-application/pending',
    name: 'TransferApplicationPending',
    component: () => import('../views/TransferApplication.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher', 'hoi', 'sdeo', 'deo']
    }
  },
  {
    path: '/transfer-application/processing',
    name: 'TransferApplicationProcessing',
    component: () => import('../views/TransferApplication.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher', 'hoi', 'sdeo', 'deo']
    }
  },
  {
    path: '/transfer-application/approved',
    name: 'TransferApplicationApproved',
    component: () => import('../views/TransferApplication.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher', 'hoi', 'sdeo', 'deo']
    }
  },
  {
    path: '/transfer-application/rejected',
    name: 'TransferApplicationRejected',
    component: () => import('../views/TransferApplication.vue'),
    meta: { 
      requiresAuth: true,
      roles: ['teacher', 'hoi', 'sdeo', 'deo']
    }
  },
  {
    path: '/transfer-application/manual',
    name: 'ManualTransfer',
    component: () => import('../views/ManualTransfer.vue'),
    meta: { 
      requiresAuth: true,
      roles: withAdminRoles()
    }
  },
  {
    path: '/medical-records',
    name: 'MedicalRecords',
    component: MedicalRecords,
    meta: { 
      requiresAuth: true,
      permissions: ['medical_records.read']
    }
  },
  {
    path: '/phone-change-requests',
    name: 'PhoneChangeRequests',
    component: () => import('../views/AdminPhoneChangeRequests.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'admin', 'admin_directorate', 'admin_secretariat', 'assistant', 'superintendent', 'deputy_director', 'joint_director', 'under_secretary', 'deputy_secretary', 'joint_secretary', 'additional_secretary', 'commissioner']
    }
  },
  {
    path: '/teachers/:id/medical-records',
    name: 'TeacherMedicalRecords',
    component: MedicalRecords,
    props: true,
    meta: { 
      requiresAuth: true,
      permissions: ['medical_records.read']
    }
  },
    {
      path: '/master-data',
      name: 'MasterData',
      component: MasterData,
      meta: {
        requiresAuth: true,
        permissions: ['master_data.read']
      }
    },
  {
    path: '/user-management',
    name: 'UserManagement',
    component: UserManagement,
    meta: { 
      requiresAuth: true,
      roles: [ROLES.SUPER_ADMIN]
    }
  },
  {
    path: '/transfer-reports',
    name: 'TransferReports',
    component: TransferReports,
    meta: {
      requiresAuth: true,
      permissions: ['analytics.read']
    }
  },
  {
    path: '/audit-logs',
    name: 'AuditLogs',
    component: AuditLogs,
    meta: {
      requiresAuth: true,
      roles: ['super_admin']
    }
  },
  {
    path: '/menu-management',
    name: 'MenuManagement',
    component: MenuManagement,
    meta: {
      requiresAuth: true,
      roles: ['super_admin']
    }
  },
  {
    path: '/workflow-management',
    name: 'WorkflowManagement',
    component: WorkflowManagement,
    meta: {
      requiresAuth: true,
      roles: ['super_admin']
    }
  },
  {
    path: '/hard-delete-requests',
    name: 'HardDeleteRequests',
    component: HardDeleteRequests,
    meta: {
      requiresAuth: true,
      roles: ['super_admin']
    }
  },
  {
    path: '/dev/mu-tch-ingest',
    name: 'MuTchIngest',
    component: MuTchIngest,
    meta: {
      requiresAuth: true,
      roles: ['super_admin']
    }
  },
  {
    path: '/transfer-order',
    name: 'TransferOrderManagement',
    component: TransferOrderManagement,
    meta: {
      requiresAuth: true,
      roles: withAdminRoles()
    }
  },
  {
    path: '/mla-badge-approval',
    name: 'MLABadgeApproval',
    component: () => import('../views/MLABadgeApproval.vue'),
    meta: {
      requiresAuth: true,
      roles: withAdminRoles('deo', 'sdeo')
    }
  },
  {
    path: '/priority-badge-approval',
    name: 'PriorityBadgeApproval',
    component: () => import('../views/PriorityBadgeApproval.vue'),
    meta: {
      requiresAuth: true,
      roles: withAdminRoles('deo', 'sdeo')
    }
  },
  {
    path: '/education-qualifications-review',
    name: 'EducationQualificationsReview',
    component: () => import('../views/EducationQualificationsReview.vue'),
    meta: {
      requiresAuth: true,
      roles: withAdminRoles('deo', 'sdeo', 'hoi', 'minister')
    }
  },
  {
    path: '/profile-approvals',
    name: 'AdminProfileApprovals',
    component: () => import('../views/AdminProfileApprovals.vue'),
    meta: {
      requiresAuth: true,
      roles: withAdminRoles('deo', 'sdeo', 'hoi', 'minister')
    }
  },
  {
    path: '/school-update-requests',
    name: 'AdminSchoolUpdateApprovals',
    component: () => import('../views/AdminSchoolUpdateApprovals.vue'),
    meta: {
      requiresAuth: true,
      roles: withAdminRoles()
    }
  },
  {
    path: '/transfer-application-approval',
    name: 'TransferApplicationApproval',
    component: TransferApplicationApproval,
    meta: {
      requiresAuth: true,
      roles: ['hoi', 'sdeo', 'deo', 'minister', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/minister-oversight',
    redirect: '/minister/retirement-forecast'
  },
  {
    path: '/minister/retirement-forecast',
    name: 'RetirementForecast',
    component: () => import('../views/minister/RetirementForecast.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister']
    }
  },
  {
    path: '/minister/vacancy-projection',
    name: 'VacancyProjection',
    component: () => import('../views/minister/VacancyProjection.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister']
    }
  },
  {
    path: '/minister/district-shortage',
    name: 'DistrictShortage',
    component: () => import('../views/minister/DistrictShortage.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister']
    }
  },
  {
    path: '/minister/recruitment-suggestions',
    name: 'RecruitmentSuggestions',
    component: () => import('../views/minister/RecruitmentSuggestions.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister']
    }
  },
  {
    path: '/track-status',
    name: 'TrackStatus',
    component: () => import('../views/TrackStatus.vue'),
    meta: {
      requiresAuth: true,
      roles: ['teacher']
    }
  },
  {
    path: '/approver-assignment',
    name: 'ApproverAssignment',
    component: ApproverAssignment,
    meta: {
      requiresAuth: true,
      roles: withAdminRoles()
    }
  },
  {
    path: '/grievances',
    name: 'TeacherGrievance',
    component: () => import('../views/TeacherGrievance.vue'),
    meta: {
      requiresAuth: true,
      roles: ['teacher']
    }
  },
  {
    path: '/grievances/admin',
    name: 'AdminGrievance',
    component: () => import('../views/AdminGrievance.vue'),
    meta: {
      requiresAuth: true,
      roles: withAdminRoles('deo', 'sdeo', 'hoi', 'minister')
    }
  },
  {
    path: '/joining-reports',
    name: 'JoiningReportsList',
    component: JoiningReportsList,
    meta: { 
      requiresAuth: true,
      roles: ['hoi', 'sdeo', 'deo', 'teacher', 'minister', 'secretariat', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/joining-reports/upload',
    name: 'JoiningReportUpload',
    component: JoiningReportUpload,
    meta: { 
      requiresAuth: true,
      roles: ['teacher']
    }
  },
  {
    path: '/aging-analytics',
    name: 'AgingAnalyticsDashboard',
    component: () => import('../views/AgingAnalyticsDashboard.vue'),
    meta: {
      requiresAuth: true,
      roles: ['super_admin', 'minister', 'admin_directorate', 'admin_secretariat', 'secretariat', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  // Non-Teaching Staff Management
  {
    path: '/non-teaching-staff',
    name: 'NonTeachingStaff',
    component: NonTeachingStaff,
    meta: {
      requiresAuth: true,
      roles: withAdminRoles()
    }
  },
  // Staff ID Card Management
  {
    path: '/staff-id/generate',
    name: 'StaffIDGenerate',
    component: StaffIDGenerate,
    meta: {
      requiresAuth: true,
      roles: withAdminRoles()
    }
  },
  {
    path: '/staff-id/list',
    name: 'StaffIDList',
    component: StaffIDList,
    meta: {
      requiresAuth: true,
      roles: withAdminRoles()
    }
  },
  {
    path: '/staff-id/verify',
    name: 'StaffIDVerify',
    component: StaffIDVerify,
    meta: {
      requiresAuth: false // Public verification page
    }
  },
  {
    path: '/posting-histories',
    name: 'AdminPostingHistory',
    component: () => import('../views/PostingHistory.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister', 'hoi', 'deo', 'sdeo', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/teachers/non-active',
    name: 'NonActiveTeachers',
    component: () => import('../views/NonActiveTeachers.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister', 'hoi', 'deo', 'sdeo', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/schools/non-active',
    name: 'NonActiveSchools',
    component: () => import('../views/NonActiveSchools.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister', 'hoi', 'deo', 'sdeo', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/teachers/active-suspended',
    name: 'ActiveSuspendedTeachers',
    component: () => import('../views/ActiveSuspendedTeachers.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister', 'hoi', 'deo', 'sdeo', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/suspension-history',
    name: 'SuspensionHistory',
    component: () => import('../views/SuspensionHistory.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister', 'hoi', 'deo', 'sdeo', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  {
    path: '/promotion-history',
    name: 'PromotionHistory',
    component: () => import('../views/PromotionHistory.vue'),
    meta: {
      requiresAuth: true,
      roles: ['minister', 'hoi', 'deo', 'sdeo', ...ADMIN_ROLES_WITH_LEGACY_MUTABLE]
    }
  },
  // Catch-all route - should never match if routes are correct
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: Login // Fallback to login for unmatched routes
  }
]

const router = createRouter({
  // Default: HTML5 history (matches Plesk/nginx proxying /schools, /reset-password, etc. to Node).
  // Set VITE_HASH_ROUTING=true at build time only on hosts without SPA path fallback.
  history: import.meta.env.VITE_HASH_ROUTING === 'true'
    ? createWebHashHistory()
    : createWebHistory(),
  routes
})

// Track if session has been validated this page load (avoids redundant token-status calls)
let sessionValidatedThisLoad = false

type AuthStore = ReturnType<typeof useAuthStore>

/**
 * Validates HTTP-only session cookies via token-status (and refresh when applicable).
 * Clears auth on failure. When it returns true, `sessionValidatedThisLoad` is set.
 * Used from public login routes (before redirecting to /) and from protected routes.
 */
async function validateSessionCookie(auth: AuthStore): Promise<boolean> {
  if (sessionValidatedThisLoad) return true
  try {
    const response = await fetch('/api/sessions/token-status', {
      method: 'GET',
      credentials: 'include'
    })
    if (!response.ok) {
      auth.clearAuthState()
      return false
    }
    const body = await response.json().catch(() => null)
    const tokenExpiration = body?.data?.tokenExpiration
    const accessExpired = tokenExpiration?.isExpired === true
    const hasRefreshToken = body?.data?.hasRefreshToken === true
    const csrfForRefresh =
      (body?.data?.csrfToken as string) || getCsrfToken() || ''

    if (accessExpired && !hasRefreshToken) {
      auth.clearAuthState()
      return false
    }

    if (accessExpired && hasRefreshToken) {
      const refreshResp = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfForRefresh ? { 'X-CSRF-Token': csrfForRefresh } : {})
        },
        body: '{}'
      })
      if (!refreshResp.ok) {
        auth.clearAuthState()
        return false
      }
      try {
        const refreshJson = await refreshResp.json()
        const newCsrf = refreshJson?.data?.csrf as string | undefined
        if (newCsrf) {
          localStorage.setItem('csrf', newCsrf)
          auth.csrf = newCsrf
        }
      } catch {
        /* ignore */
      }
    }

    sessionValidatedThisLoad = true
    return true
  } catch {
    auth.clearAuthState()
    return false
  }
}

// Navigation guard to protect routes
router.beforeEach(async (to, from, next) => {
  const auth = useAuthStore()

  // Toggle UX4G accessibility widget visibility per route (hide on auth/public screens)
  const hideAccessibilityOnRoutes = ['/login', '/otp-login', '/forgot-password', '/reset-password']
  if (typeof document !== 'undefined') {
    if (hideAccessibilityOnRoutes.includes(to.path)) {
      document.body.classList.add('hide-accessibility-widget')
    } else {
      document.body.classList.remove('hide-accessibility-widget')
    }
  }

  // Check if route requires authentication
  const requiresAuth = to.meta.requiresAuth

  // CRITICAL: Explicitly check for reset-password route FIRST (before any other checks)
  if (to.path === '/reset-password' || to.fullPath.startsWith('/reset-password')) {
    // Always allow reset-password route - it's public
    next()
    return
  }

  // Allow access to public pages (including reset-password) without any auth checks
  if (requiresAuth === false) {
    // Only initialize auth store for login pages if needed
    if (to.path === '/login' || to.path === '/otp-login') {
      if (!auth.isAuthenticated) {
        auth.initializeFromStorage()
      }
      const isAuthenticated = auth.isLoggedIn
      const currentUser = auth.currentUser

      if (isAuthenticated) {
        // Avoid flashing the main app shell: localStorage can say "logged in" while cookies
        // are expired. Only redirect after the session is confirmed (same logic as protected routes).
        const roleBefore = currentUser?.role
        const ok = await validateSessionCookie(auth)
        if (ok) {
          next('/')
          return
        }
        const wasTeacher =
          roleBefore && String(roleBefore).trim().toLowerCase() === 'teacher'
        if (wasTeacher) {
          next({ path: '/login', query: { focus: 'teacher' } })
        } else {
          next()
        }
        return
      } else if (to.path === '/login' && currentUser && currentUser.role === 'teacher') {
        next({ path: '/login', query: { focus: 'teacher' } })
        return
      }
    }
    // For other public pages (like /reset-password, /forgot-password), allow access
    next()
    return
  }

  // Protected routes: initialize auth store if needed
  if (!auth.isAuthenticated) {
    auth.initializeFromStorage()
  }
  const isAuthenticated = auth.isLoggedIn
  const currentUser = auth.currentUser

  // Check authentication for protected routes
  if (!isAuthenticated) {
    next('/login')
    return
  }

  // Validate session with backend on first protected route access (prevents showing
  // authenticated UI when access token cookie is expired or cleared).
  if (!sessionValidatedThisLoad) {
    const roleSnapshot = currentUser?.role
    const ok = await validateSessionCookie(auth)
    if (!ok) {
      const wasTeacher =
        roleSnapshot && String(roleSnapshot).trim().toLowerCase() === 'teacher'
      next(wasTeacher ? { path: '/login', query: { focus: 'teacher' } } : '/login')
      return
    }
  }

  // Check role-based access
  if (to.meta.roles && currentUser) {
    const userRole = currentUser.role
    const requiredRoles = to.meta.roles as string[]
    
    // Check if user has required role
    if (!requiredRoles.includes(userRole)) {
      // Special case: redirect admins and other non-teacher roles from /grievances to /grievances/admin
      if (to.path === '/grievances' && withAdminRoles('deo', 'sdeo', 'hoi', 'minister').includes(userRole as any)) {
        next('/grievances/admin')
        return
      }
      
      // User doesn't have required role, redirect to dashboard with error
      next({ 
        path: '/', 
        query: { error: 'insufficient_permissions' }
      })
      return
    }
  }
  
  // Check permission-based access
  if (to.meta.permissions && currentUser) {
    const userRole = currentUser.role
    const requiredPermissions = to.meta.permissions
    
    const hasRequiredPermissions = requiredPermissions.every(permission => 
      hasPermission(userRole, permission)
    )
    
    if (!hasRequiredPermissions) {
      // User doesn't have required permissions, redirect to dashboard with error
      next({ 
        path: '/', 
        query: { error: 'insufficient_permissions' }
      })
      return
    }
  }
  
  // All checks passed, allow access
  next()
})

export default router
